import { useState, useEffect } from 'react';
import logger from '../../lib/logger';
import AdminLayout from '../../components/layout/AdminLayout';
import { EdicaoConfigCard } from '../../components/admin/EdicaoConfigCard';
import { Settings, Mail, Save, AlertCircle, CheckCircle, Users, Target } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ConfiguracoesPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingVagas, setSavingVagas] = useState(false);
  const [config, setConfig] = useState({
    email_lgpd: '',
    max_vagas_evento: 58,
    updated_at: null,
    updated_by: null
  });
  const [emailError, setEmailError] = useState('');
  const [vagasError, setVagasError] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConfig({
          email_lgpd: data.email_lgpd || '',
          max_vagas_evento: data.max_vagas_evento || 58,
          updated_at: data.updated_at,
          updated_by: data.updated_by
        });
      }
    } catch (error) {
      logger.error('Error fetching config:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setConfig(prev => ({ ...prev, email_lgpd: email }));
    
    if (email && !validateEmail(email)) {
      setEmailError('Formato de e-mail inválido');
    } else {
      setEmailError('');
    }
  };

  const handleVagasChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setConfig(prev => ({ ...prev, max_vagas_evento: value }));
    
    if (value < 1) {
      setVagasError('Mínimo de 1 vaga');
    } else if (value > 500) {
      setVagasError('Máximo de 500 vagas');
    } else {
      setVagasError('');
    }
  };

  const handleSave = async () => {
    if (!config.email_lgpd) {
      setEmailError('E-mail é obrigatório');
      return;
    }

    if (!validateEmail(config.email_lgpd)) {
      setEmailError('Formato de e-mail inválido');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email_lgpd: config.email_lgpd })
      });

      if (response.ok) {
        toast.success('E-mail atualizado com sucesso!');
        fetchConfig();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar configuração');
      }
    } catch (error) {
      logger.error('Error saving config:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVagas = async () => {
    if (vagasError) return;

    setSavingVagas(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ max_vagas_evento: config.max_vagas_evento })
      });

      if (response.ok) {
        toast.success('Limite de vagas atualizado!');
        fetchConfig();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar configuração');
      }
    } catch (error) {
      logger.error('Error saving vagas:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingVagas(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="configuracoes-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-kairos-orange/10 flex items-center justify-center">
            <Settings className="w-6 h-6 text-kairos-orange" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Configurações</h1>
            <p className="text-gray-400">Gerencie as configurações do site</p>
          </div>
        </div>

        <EdicaoConfigCard />

        {/* LGPD Settings Card */}
        <Card className="bg-obsidian-100 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Tema Jurídico / LGPD</CardTitle>
                <CardDescription className="text-gray-400">
                  Configurações relacionadas à Política de Privacidade
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email_lgpd" className="text-gray-300">
                E-mail de contato para Política de Privacidade
              </Label>
              <div className="relative">
                <Input
                  id="email_lgpd"
                  type="email"
                  placeholder="contato@seudominio.com.br"
                  value={config.email_lgpd}
                  onChange={handleEmailChange}
                  className={`bg-obsidian border-white/10 text-white placeholder:text-gray-500 pr-10 ${
                    emailError ? 'border-red-500 focus:border-red-500' : ''
                  }`}
                  data-testid="email-lgpd-input"
                />
                {config.email_lgpd && !emailError && (
                  <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {emailError && (
                  <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {emailError && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {emailError}
                </p>
              )}
              <p className="text-gray-500 text-sm">
                Este e-mail será exibido na página de Política de Privacidade como canal de contato do controlador de dados (LGPD).
              </p>
            </div>

            {/* Last Update Info */}
            {config.updated_at && (
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <p className="text-gray-400 text-sm">
                  <span className="text-gray-500">Última atualização:</span>{' '}
                  {formatDate(config.updated_at)}
                  {config.updated_by && (
                    <span className="text-gray-500"> por {config.updated_by}</span>
                  )}
                </p>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-white/10">
              <Button
                onClick={handleSave}
                disabled={saving || emailError || !config.email_lgpd}
                className="bg-kairos-orange hover:bg-kairos-orange-dark text-white px-6"
                data-testid="save-config-btn"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar alterações
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Capacity Settings Card */}
        <Card className="bg-obsidian-100 border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-kairos-orange/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-kairos-orange" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Capacidade do Evento</CardTitle>
                <CardDescription className="text-gray-400">
                  Limite máximo de inscrições permitidas
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Max Vagas Input */}
            <div className="space-y-2">
              <Label htmlFor="max_vagas" className="text-gray-300">
                Número máximo de vagas
              </Label>
              <div className="flex items-center gap-4">
                <div className="relative w-32">
                  <Input
                    id="max_vagas"
                    type="number"
                    min="1"
                    max="500"
                    value={config.max_vagas_evento}
                    onChange={handleVagasChange}
                    className={`bg-obsidian border-white/10 text-white text-center text-lg ${
                      vagasError ? 'border-red-500 focus:border-red-500' : ''
                    }`}
                    data-testid="max-vagas-input"
                  />
                </div>
                <span className="text-gray-400">vagas</span>
              </div>
              {vagasError && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {vagasError}
                </p>
              )}
              <p className="text-gray-500 text-sm">
                Quando o número de inscrições (aprovadas + pendentes válidas) atingir este limite, 
                os botões de inscrição serão desabilitados automaticamente.
              </p>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <p className="text-yellow-400 text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Reservas pendentes (pagamentos em andamento) ocupam vaga por 30 minutos. 
                  Após esse período, a vaga é liberada automaticamente se o pagamento não for confirmado.
                </span>
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-white/10">
              <Button
                onClick={handleSaveVagas}
                disabled={savingVagas || vagasError}
                className="bg-kairos-orange hover:bg-kairos-orange-dark text-white px-6"
                data-testid="save-vagas-btn"
              >
                {savingVagas ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar limite de vagas
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ConfiguracoesPage;
